import React, { useEffect, useState } from 'react';
import AdminLayout from '../../../components/layout/AdminLayout';
import Card from '../../../components/common/Card';
import RangeTable from '../../../components/tables/RangeTable';
import Button from '../../../components/common/Button';
import FormField from '../../../components/common/FormField';
import { useRangeApi } from '../../../hooks/useRangeApi';
import AddNewButton from '../../../components/common/AddButton';
import { confirmDelete } from '../../../utils/sweetAlertUtils';
import { RangeDTO } from '../../../services/apiClient';
import { useNavigate } from 'react-router-dom';
import { getRoles } from '../../../utils/tokenUtils';
import { useRangeStatuses } from '../../../hooks/useRangeStatusApi';
import { useRangeTypes } from '../../../hooks/useRangeTypeApi';
import Drawer from '../../../components/common/Drawer';
import FeedbackForm from '../../../components/forms/FeedbackForm';
import { ComponentConstants } from '../../../constants/ComponentConstants';
import { RangeStatusConstants } from '../../../constants/RangeStatusConstants';
import { DrawerViewConstants, RangeDrawerViews } from '../../../constants/DrawerViewConstants';
import EmailDrawerContent from '../../../components/common/EmailDrawerContent';
import { useEmailHandlers } from '../../../hooks/useEmailHandlers';
import { useEmailMessageApi } from '../../../hooks/useEmailMessageApi';
import { useSearchState } from '../../../utils/searchStateUtils/useSearchState';
import { getAdjustedPageAfterMutation } from '../../../utils/paginationUtils';

const RangeList: React.FC = () => {
  const { edit, search, remove, updateStatus, isLoading, error } = useRangeApi();
  const [items, setItems] = useState<RangeDTO[]>([]);
  
  // Search state persistence
  const {
    currentPage,
    pageSize,
    sortBy,
    sortDescending,
    searchFilters,
    setCurrentPage,
    setPageSize,
    setSortBy,
    setSortDescending,
    setSort,
    setSearchFilters,
    showAdvanced,
    setShowAdvanced,
    clearSearchState,
  } = useSearchState('ranges', {
    currentPage: 1,
    pageSize: 10,
    sortBy: 'CreatedUtc',
    sortDescending: true,
    searchFilters: {},
    showAdvanced: false,
  });
  
  const [totalRecords, setTotalRecords] = useState(0);
  const [rangeTypeOptions, setRangeTypeOptions] = useState<{ value: string; label: string }[]>([]);
  const [rangeStatusOptions, setRangeStatusOptions] = useState<{ value: string; label: string }[]>(
    [],
  );
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
  const [drawerView, setDrawerView] = useState<RangeDrawerViews>();
  const [selectedRange, setSelectedRange] = useState<RangeDTO>({});
  const [defaultStatusId, setDefaultStatusId] = useState<string>();
  const [isSubmitingFeedback, setIsSubmittingFeedback] = useState<boolean>(false);
  const [drawerTitle, setDrawerTitle] = useState<string>('Approve');
  
  const statusFieldName = 'rangeStatusId';

  const roles = getRoles();
  const navigate = useNavigate();
  const breadcrumbs = [
    { label: 'Admin', href: '#' },
    { label: 'Ranges', href: '#' },
  ];

  const { data: rangeTypesData } = useRangeTypes();
  const { data: rangeStatusesData } = useRangeStatuses();
  const { sendEmail, isLoading: isSendingEmail } = useEmailMessageApi();

  useEffect(() => {
    setRangeTypeOptions(
      rangeTypesData?.map((type) => ({
        value: type.id || '',
        label: type.title || '',
      })) || [],
    );
  }, [rangeTypesData]);

  useEffect(() => {
    setRangeStatusOptions(
      rangeStatusesData?.map((status) => ({
        value: status.id || '',
        label: status.title || '',
      })) || [],
    );
  }, [rangeStatusesData]);

  const fetchItems = async (page: number, size: number, filters: any) => {
    try {
      const response = await search({
        filters,
        page,
        pageSize: size,
        sortBy: sortBy,
        sortDescending: sortDescending,
      });
      setItems(response?.data?.results || []);
      setTotalRecords(response?.data?.totalCount || 0);
      return response?.data?.totalCount || 0;
    } catch (error) {
      console.error('Error fetching Ranges:', error);
      return 0;
    }
  };

  // Email handlers from useEmailHandlers hook
  const {
    handleSendEmail,
    handleSendEmailSubmit,
    handleViewAllEmails,
    handleViewRecordEmails,
  } = useEmailHandlers<RangeDTO>({
    setSelectedItem: setSelectedRange,
    setDrawerTitle,
    setDrawerView,
    setIsDrawerOpen,
    sendEmail,
    fetchItems: async (page: number, size: number, filters: any) => {
      await fetchItems(page, size, filters);
    },
    currentPage,
    pageSize,
    searchFilters,
  });

  const handleSort = (sortKey: string) => {
    if (sortBy === sortKey) {
      setSort(sortKey, !sortDescending);
    } else {
      setSort(sortKey, true);
    }
  };

  /**
   * Refreshes the list after a mutation (delete, update, etc.),
   * automatically adjusting to a valid page if the current page is now out of range.
   */
  const refreshAfterMutation = async () => {
    // Fetch with current page first to get the new total count
    const newTotalCount = await fetchItems(currentPage, pageSize, searchFilters);
    
    // Calculate what page we should be on based on new total
    const adjustedPage = getAdjustedPageAfterMutation(currentPage, pageSize, newTotalCount);
    
    // If we need to move to a different page, update and refetch
    if (adjustedPage !== currentPage) {
      setCurrentPage(adjustedPage);
      await fetchItems(adjustedPage, pageSize, searchFilters);
    }
  };

  useEffect(() => {
    fetchItems(currentPage, pageSize, searchFilters);
  }, [currentPage, pageSize, sortBy, sortDescending]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchItems(1, pageSize, searchFilters);
  };

  const handleClearSearch = () => {
    clearSearchState();
  };

  const handleDelete = async (dto: RangeDTO) => {
    const confirmed = await confirmDelete();
    if (!confirmed) return;

    try {
      await remove(dto.id || '');
      await refreshAfterMutation();
    } catch (error) {
      console.error('Error deleting Range:', error);
    }
  };

  const handleApprove = (range: RangeDTO) => {
    setSelectedRange(range);
    setDefaultStatusId(RangeStatusConstants.APPROVED);
    setDrawerTitle('Approve');
    setDrawerView(DrawerViewConstants.FEEDBACK);
    setIsDrawerOpen(true);
  };

  const handleChangeStatus = (range: RangeDTO) => {
    setSelectedRange(range);
    setDrawerTitle('Change Status');
    setDrawerView(DrawerViewConstants.FEEDBACK);
    setIsDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setIsDrawerOpen(false);
    setSelectedRange({});
    setDefaultStatusId(undefined);
  };

  const handleApproveSubmit = async (data: any) => {
    try {
      setIsSubmittingFeedback(true);

      // Prepare update DTO for single range
      const updateDto = {
        recordIds: [selectedRange.id!],
        rangeStatusId: data.rangeStatusId,
        feedback: data.feedback,
        replyTemplateId: data.replyTemplateId,
        sendFeedbackEmail: data.sendFeedbackEmail || false,
      };

      // Call the update endpoint
      await updateStatus(updateDto);

      navigate('/admin/ranges');
      setIsDrawerOpen(false);
      setSelectedRange({});
      await refreshAfterMutation();
    } catch (error) {
      console.error('Error submitting feedback:', error);
    } finally {
      setIsSubmittingFeedback(false);
    }
  };

  return (
    <AdminLayout
      activeMenuItem="/admin/ranges"
      breadcrumbs={breadcrumbs}
      allowedRoles={['Admin']}
      requiredPermission="Manage.Range.View"
    >
      <Card
        title="Manage Ranges"
        subtitle={`Total Records: ${totalRecords}`}
        headerActions={
          <div className="flex space-x-2">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setShowAdvanced(!showAdvanced)}
            >
              {showAdvanced ? 'Hide Advanced Search' : 'Advanced Search'}
            </Button>
            <AddNewButton onClick={() => navigate('/admin/ranges/create')} />
          </div>
        }
      >
        <form onSubmit={handleSearch} className="mb-4">
          {showAdvanced && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <FormField
                id="rangeTypeId"
                label="Search by Range Type"
                type="dropdown"
                value={searchFilters.rangeTypeId || ''}
                onChange={(e) =>
                  setSearchFilters({
                    ...searchFilters,
                    rangeTypeId: e.target.value,
                  })
                }
                placeholder="All Range Types"
                options={rangeTypeOptions}
              />
              <FormField
                id="rangeStatusId"
                label="Search by Range Status"
                type="dropdown"
                value={searchFilters.rangeStatusId || ''}
                onChange={(e) =>
                  setSearchFilters({
                    ...searchFilters,
                    rangeStatusId: e.target.value,
                  })
                }
                placeholder="All Range Statuses"
                options={rangeStatusOptions}
              />
              <FormField
                id="title"
                label="Search by Title"
                value={searchFilters.title || ''}
                onChange={(e) => setSearchFilters({ ...searchFilters, title: e.target.value })}
                placeholder="Search by Title"
              />
              <FormField
                id="createdFrom"
                label="Created From"
                type="date"
                value={searchFilters.createdFrom || ''}
                onChange={(e) =>
                  setSearchFilters({ ...searchFilters, createdFrom: e.target.value })
                }
              />
              <FormField
                id="createdTo"
                label="Created To"
                type="date"
                value={searchFilters.createdTo || ''}
                onChange={(e) =>
                  setSearchFilters({ ...searchFilters, createdTo: e.target.value })
                }
              />
            </div>
          )}

          <div className="flex space-x-4 mb-4">
            {showAdvanced && (
              <>
                <Button variant="secondary" onClick={handleClearSearch}>
                  Clear Search
                </Button>
                <Button type="submit">Search</Button>
              </>
            )}
          </div>
        </form>

        <RangeTable
          data={items}
          handleEdit={(dto) => navigate(`/admin/ranges/${dto.id}`)}
          handleDelete={handleDelete}
          handleSendEmail={handleSendEmail}
          handleViewAllEmails={handleViewAllEmails}
          handleViewRecordEmails={handleViewRecordEmails}
          handleApprove={handleApprove}
          handleChangeStatus={handleChangeStatus}
          currentPage={currentPage}
          totalPages={Math.ceil(totalRecords / pageSize)}
          setCurrentPage={setCurrentPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          isLoading={isLoading}
          sortBy={sortBy}
          sortDescending={sortDescending}
          onSort={handleSort}
        />

        <Drawer
          isOpen={isDrawerOpen}
          onClose={handleDrawerClose}
          title={drawerTitle}
          size='large'
        >
          {drawerView === DrawerViewConstants.FEEDBACK && (
            <FeedbackForm
              componentId={ComponentConstants.RANGES}
              recordId={selectedRange.id ?? ''}
              componentStatuses={rangeStatusOptions}
              statusFieldName={statusFieldName}
              defaultStatusId={defaultStatusId}
              initialData={selectedRange}
              onSubmit={handleApproveSubmit}
              onCancel={handleDrawerClose}
              updateLoading={isSubmitingFeedback}
            />
          )}
          <EmailDrawerContent
            drawerView={drawerView}
            selectedItem={selectedRange}
            componentId={ComponentConstants.RANGES}
            getMemberId={(dto) => dto?.member?.id || ''}
            onEmailSubmit={handleSendEmailSubmit}
            onClose={handleDrawerClose}
            isSending={isSendingEmail}
          />
        </Drawer>
      </Card>
    </AdminLayout>
  );
};

export default RangeList;
